import { useTooltip } from "context/TooltipProvider";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { addAppealDailyReport } from "services/appealService";
import {
  AppealDailyReport,
  AppealDailyReportStatus,
  AppealRequest,
  DefendantAppeal,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  appealData?: AppealRequest;
  defendantAppealData?: DefendantAppeal;
  inline?: boolean;
  onClose: (refetch: boolean) => void;
};

const defaultValue: AppealDailyReport = {
  appeal_request: 0,
  status: "pending",
  next_trial_appointment: undefined,
  appointment_type: "",
  appointment_reason: "",
  dailyreport_summary: "",
  court_order_for_next_appointment: "",
  is_final_verdict_given: false,
};

const AppealAddDailyReport: FC<Props> = ({
  appealData,
  defendantAppealData,
  inline,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<AppealDailyReport>({
    ...defaultValue,
    appeal_request: appealData?.id,
    defendant_appeal_request: defendantAppealData?.id,
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (formState.next_trial_appointment) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const trialDate = new Date(formState.next_trial_appointment);
      trialDate.setHours(0, 0, 0, 0);
      if (trialDate <= today) {
        return t("next_trial_appointment_validation");
      }
    }
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);

    addAppealDailyReport(formState)
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={inline ? undefined : { marginTop: "0px" }}
      className={
        inline
          ? "w-full"
          : "fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      }
      onClick={inline ? undefined : handleClickOutside}
    >
      <div
        className={
          inline
            ? "relative flex w-full flex-col rounded-xl bg-white border border-gray-200 shadow-sm"
            : "relative flex w-full max-w-2xl flex-col overflow-y-auto rounded-xl bg-white shadow-xl"
        }
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-100 bg-white px-8 py-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-1.5 rounded-full bg-red-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t("appeal_daily_report")}
              </h2>
              <p className="mt-0.5 text-sm text-gray-500">
                {t("fill_appeal_daily_report_details")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onClose(false)}
            aria-label="Close"
            className="shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  {t("report_date")}
                </label>
                <input
                  type="date"
                  className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                  name="report_date"
                  value={
                    formState?.report_date
                      ? new Date(formState.report_date)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  min={new Date().toISOString().split("T")[0]}
                  onChange={handleChange}
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  {t("next_trial_appointment")}
                </label>
                <input
                  type="date"
                  className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                  name="next_trial_appointment"
                  value={
                    formState?.next_trial_appointment
                      ? new Date(formState.next_trial_appointment)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  min={new Date().toISOString().split("T")[0]}
                  onChange={handleChange}
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  {t("appointment_type")}
                </label>
                <input
                  type="text"
                  placeholder={t("appointment_type_placeholder")}
                  className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                  name="appointment_type"
                  value={formState?.appointment_type || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  {t("appointment_reason")}
                </label>
                <input
                  type="text"
                  placeholder={t("appointment_reason_placeholder")}
                  className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                  name="appointment_reason"
                  value={formState?.appointment_reason || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="flex flex-col sm:col-span-2">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  {t("status")}
                </label>
                <select
                  className="bg-white border border-gray-300 rounded-lg p-2 pr-8 focus:outline-none focus:ring focus:ring-red-200"
                  name="status"
                  value={formState?.status || ""}
                  onChange={handleChange}
                >
                  <option value={""}>{t("select_status")}</option>
                  {Object.entries(AppealDailyReportStatus).map(
                    ([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ),
                  )}
                </select>
              </div>
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">
                {t("daily_report_summary")}
              </label>
              <textarea
                rows={4}
                placeholder={t("daily_report_summary_placeholder")}
                className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                name="dailyreport_summary"
                value={formState?.dailyreport_summary || ""}
                onChange={handleChange}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">
                {t("court_order_next_appointment")}
              </label>
              <textarea
                rows={4}
                placeholder={t("court_order_next_appointment_placeholder")}
                className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                name="court_order_for_next_appointment"
                value={formState?.court_order_for_next_appointment || ""}
                onChange={handleChange}
              />
            </div>

            <label className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded text-red-600 focus:ring-red-400"
                checked={Boolean(formState?.is_final_verdict_given)}
                onChange={() =>
                  setFormState((prevState) => ({
                    ...prevState,
                    is_final_verdict_given: !prevState.is_final_verdict_given,
                  }))
                }
              />
              <span className="text-sm font-medium text-gray-700">
                {t("is_final_verdict_given")}
              </span>
            </label>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-medium"
              >
                {t("cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="submit"
                className={`inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    {LoadingIcon}
                    {t("loading")}
                  </>
                ) : (
                  t("submit")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AppealAddDailyReport;
